import React, { useState } from 'react';
import withSizes from 'react-sizes';

import * as Types from 'types';
import { Artwork } from 'components/artworks/Artwork';
import { SvgIcon } from 'components/icons/SvgIcon';
import { MaskedImage } from 'components/MaskedImage';
import { SelectableContent } from 'components/SelectableContent';
import { ArtworkPreviewPlayer } from 'player/ArtworkPreviewPlayer';
import { breakpoints } from 'services/theme';

import { DeviceArtworkTitle } from './DeviceArtworkTitle';
import { getMaskedImageConfigForInterior } from './getMaskedImageConfigForInterior';

type HOCProps = {
  isWindowWidthStd: boolean;
  isWindowWidthMd: boolean;
  isWindowWidthLg: boolean;
};

export type Props = {
  playlist: Types.Playlist;
  interior: 'loft' | 'moscow' | 'mac';
};

const MAX_ARTWORKS = 4;

export const PlaylistHeroComponent: React.FC<Props & HOCProps> = (props) => {
  const { playlist, interior, isWindowWidthStd, isWindowWidthMd, isWindowWidthLg } = props;
  const items = playlist.items.slice(0, MAX_ARTWORKS);

  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [initializedPlayers, setInitializedPlayers] = useState<boolean[]>([
    true,
    ...Array(MAX_ARTWORKS - 1).fill(false),
  ]);

  const activeItem = items[activeIndex];

  return (
    <div className='playlist-hero page-hero align-items-end pos-rel'>
      <MaskedImage {...getMaskedImageConfigForInterior(interior)}>
        <div className='pos-rel w-100 h-100'>
          <DeviceArtworkTitle interior={interior} artwork={activeItem.artwork} />
          {items.map((item, index) => (
            <div
              key={index}
              style={{ opacity: index === activeIndex ? 1 : 0 }}
              className='default-transition stretch'
            >
              <ArtworkPreviewPlayer
                artworkId={item.artwork.id}
                initialize={initializedPlayers[index]}
              />
            </div>
          ))}
        </div>
      </MaskedImage>
      <div className='container-fluid pb-1 pt-5 bg-gradient-into-dark pos-rel text-light'>
        <div className='row mx-auto align-items-end' style={{ maxWidth: 1408 }}>
          <div className='col col-md-4 mr-1 mr-lg-2'>
            <h1 className='headline headline--medium headline--subtle headline--readable'>
              {playlist.name}
            </h1>
            <div className='mb-1 d-flex align-items-center'>
              <span className='key-value key-value__value'>
                <SvgIcon
                  style={{ top: '4px', fontSize: '16px' }}
                  icon='waves'
                  className='pos-rel mr-mini'
                />
                Curated playlist
              </span>
            </div>
            <p className='standfirst standfirst--subtle'>{playlist.description}</p>
          </div>
          <div
            className='col container-fluid flex-grow-1'
            style={{
              display: isWindowWidthMd ? 'flex' : 'none',
            }}
          >
            <div className='nmx-mini d-flex flex-grow-1'>
              <SelectableContent onSelectChange={setActiveIndex}>
                {({ selectedIndex, select }) =>
                  items.map((playlistArtwork, index) => (
                    <div
                      key={playlistArtwork.id}
                      className='pos-rel default-transition mx-mini'
                      onMouseOver={() => {
                        const newInitializedPlayers = [...initializedPlayers];
                        newInitializedPlayers[index] = true;
                        setInitializedPlayers(newInitializedPlayers);
                      }}
                      onClick={() => {
                        const newInitializedPlayers = [...initializedPlayers];
                        newInitializedPlayers[index] = true;
                        setInitializedPlayers(newInitializedPlayers);
                        select(index);
                      }}
                      style={{
                        cursor: 'pointer',
                        top: selectedIndex === index ? -20 : 0,
                        flex: '1 1 0px',
                        display: index === 3 && !isWindowWidthStd ? 'none' : 'block',
                      }}
                    >
                      <Artwork
                        artwork={playlistArtwork.artwork}
                        maxlength={40}
                        style={{ pointerEvents: 'none' }}
                        showArtistName={isWindowWidthLg}
                        small
                      />
                    </div>
                  ))
                }
              </SelectableContent>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const PlaylistHero = withSizes<{}, Props>(({ width }) => ({
  isWindowWidthStd: width >= breakpoints.std,
  isWindowWidthMd: width >= breakpoints.md,
  isWindowWidthLg: width >= breakpoints.lg,
  // @ts-ignore
}))(PlaylistHeroComponent);
